import { Service } from './service';

const url = {
  RESPOND: '/api/v1/vendor/call/respond',
  CAPABILITIES: '/api/v1/vendor/call/capabilities',
  /**
   * Who is in a LiveKit room right now, as `user-<id>` identities.
   *
   * los-video-service rather than vendor-service, because being on a call is a thing only
   * LiveKit knows — vendor-service tracks websocket sessions and has no view of it. This is the
   * same endpoint Meet and the mobile app read their "In a call" state from, which is what
   * keeps the three of them saying the same thing about the same person.
   */
  BUSY_IDENTITIES: '/platform-api/v1/video/presence/busy',
};

export type CallResponseType =
  | 'ACCEPTED'
  | 'DECLINED'
  | 'CANCELLED'
  | 'MISSED'
  /** Sent by the callee's own client when they are already on a call. */
  | 'BUSY';

/**
 * What the signed-in user may do in Meet, as the server decides it.
 *
 * Each question is separate because they move separately: opening a room was widened to the
 * back office before searching the directory was, and folding them into one "is this staff"
 * boolean is how the desktop and the mobile app ended up with two different answers to the same
 * question.
 */
export interface MeetCapabilities {
  /**
   * Whether this environment offers Meet in the Electron app.
   *
   * This replaced `features.loanChatMeet` in environment.ts, which was hard-coded true in all
   * three builds — so the one thing a kill switch exists for, turning the feature off in a
   * hurry, meant cutting an installer and getting it onto every desk.
   */
  desktopMeetAvailable: boolean;
  /** The same question for the mobile app, which the desktop has no use for. */
  mobileMeetAvailable: boolean;
  /**
   * May place a call at all — the line between a caller and a callee.
   *
   * Wider than canCreateRoom: Huddling someone already in your conversation opens no ad-hoc room
   * and searches no directory, so neither of the other two ever covered it.
   */
  canMakeCalls: boolean;
  canCreateRoom: boolean;
  canSearchDirectory: boolean;
  /** True for every role Meet reaches. A borrower is a callee, not a caller. */
  canReceiveCalls: boolean;
}

/**
 * Telling the other side how a call resolved.
 *
 * Lives in the chat window because that is the only part of the desktop app with a session: the
 * ring window is deliberately sandboxed with no network access, and the main process has no
 * credentials. The main process forwards the outcome here over IPC and this sends it on.
 */
export class CallService extends Service {
  /**
   * No brand parameter from the desktop.
   *
   * The brand selects which mobile kill switch to read, and this app is not on that switch —
   * sending one would subject the desktop to a flag meant for a phone build.
   */
  capabilities() {
    return this.get(url.CAPABILITIES, {}, { disableGlobalLoading: true });
  }

  busyIdentities() {
    return this.get(url.BUSY_IDENTITIES, {}, { disableGlobalLoading: true });
  }

  respond(input: {
    roomCode: string;
    response: CallResponseType;
    notifyUserId: number;
  }) {
    return this.post(url.RESPOND, input, {}, { disableGlobalLoading: true });
  }
}
